import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
} from "react";
import {
  Button,
  Explainer,
  IconDenom,
  useGlobalModalContext,
  useTranslation,
} from "rujira.ui";
import { useRedactedV2 } from "../../../redacted/v2";
import { useNewSpendingAccount } from "../NewSpendingAccount";
import {
  byWeakness,
  privacyView,
  type PrivacyAsset,
  type PrivacyTone,
} from "./privacyDisplay";
import { checkedText, levelLabel, reasonLines } from "./privacyText";
import { PrivacyTrack } from "./PrivacyTrack";
import { useNow } from "./useNow";

type LearnMore = {
  /** Every asset from the (i), one asset from its row's meter. */
  open(asset: PrivacyAsset | undefined, opener: HTMLElement): void;
};

const LearnMoreContext = createContext<LearnMore | undefined>(undefined);

/**
 * Props for a button that opens "Learn more about your privacy": the Privacy
 * column's (i) without an asset, a row's meter with its asset. Nothing
 * without the provider.
 */
export const usePrivacyLearnMore = (asset?: PrivacyAsset) => {
  const context = useContext(LearnMoreContext);
  if (!context) return undefined;
  return {
    "aria-haspopup": "dialog" as const,
    onClick: (event: { currentTarget: HTMLElement }) =>
      context.open(asset, event.currentTarget),
  };
};

/**
 * The Privacy column header: its dotted underline says there is more to
 * know (in place of an (i) icon); a click opens "Learn more".
 */
export const PrivacyLearnMoreButton: FC = () => {
  const { t } = useTranslation("portfolio");
  const learnMore = usePrivacyLearnMore();
  if (!learnMore) return <>{t("privacy")}</>;
  return (
    <button
      type="button"
      className="privacy-learn__open has-tip"
      aria-label={`${t("privacy")}: ${t("privacyLearnMore")}`}
      data-tooltip-id="global-tip"
      data-tooltip-content={t("privacyLearnMore")}
      {...learnMore}>
      {t("privacy")}
    </button>
  );
};

/**
 * "Learn more about your privacy" in the app's own modal: what the labels
 * mean, the user's assets with their reasons (only the row's asset when a
 * meter opened it) and a few tips. The modal's close button, a click on the
 * background and Escape close it; focus goes back to the button that opened
 * it. Everything shown is the session's local assessment.
 */
export const PrivacyLearnMore: FC<
  PropsWithChildren<{ assets: readonly PrivacyAsset[] }>
> = ({ assets, children }) => {
  const { showModal } = useGlobalModalContext();
  const latest = useRef(assets);
  latest.current = assets;
  const open = useCallback(
    (asset: PrivacyAsset | undefined, opener: HTMLElement) =>
      showModal({
        backgroundClose: true,
        className: "privacy-learn-modal",
        children: (
          <PrivacyLearnMorePanel
            assets={asset ? [asset] : latest.current}
            focused={!!asset}
            opener={opener}
          />
        ),
      }),
    [showModal]
  );
  const value = useMemo(() => ({ open }), [open]);
  return (
    <LearnMoreContext.Provider value={value}>
      {children}
    </LearnMoreContext.Provider>
  );
};

/** The app's color utilities for each label, as in the table. */
const TONE_COLOR: Record<PrivacyTone, string> = {
  limited: "color-orange",
  mixed: "color-lightGrey",
  "no-known-link": "color-primary6",
  unknown: "color-grey",
};

const LEVELS: readonly PrivacyTone[] = [
  "limited",
  "mixed",
  "no-known-link",
  "unknown",
];

/**
 * The panel's content, short enough for a normal popup: the assets with their
 * reasons (only the row's asset when a meter opened it), the advice to use a
 * new Spending account, the labels and a few tips. Longer explanations sit in
 * the dotted-underline explainers.
 */
export const PrivacyLearnMorePanel: FC<{
  assets: readonly PrivacyAsset[];
  /** Opened from one asset's meter. */
  focused?: boolean;
  /** Gets the focus back when the modal closes. */
  opener?: HTMLElement;
}> = ({ assets, focused = false, opener }) => {
  const { t, language } = useTranslation("portfolio");
  const { hideModal } = useGlobalModalContext();
  const { privacy } = useRedactedV2();
  const newSpending = useNewSpendingAccount();
  const titleId = `privacy-learn-${useId().replace(/[^\w-]/g, "")}`;
  const root = useRef<HTMLDivElement>(null);
  // Into the modal on open, back to the button that opened it on close.
  useEffect(() => {
    root.current?.focus({ preventScroll: true });
    return () => {
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [opener]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") hideModal();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [hideModal]);
  const now = useNow(30_000, privacy.checking ? undefined : privacy.updatedAt);
  const items = byWeakness(
    assets.map((asset) => ({
      asset,
      view: privacyView(privacy.byDenom.get(asset.denom), privacy.checking),
    }))
  );
  // An asset that is easier to trace, or a link: its next actions are better
  // kept apart from the earlier ones.
  const weak = items.some(
    ({ view }) => view.level === "limited" || view.linkDetected
  );
  // Literal keys, so every text is checked against the locales.
  const levels: Record<PrivacyTone, { label: string; meaning: string }> = {
    limited: {
      label: t("privacyLimited"),
      meaning: t("privacyMeaningLimited"),
    },
    mixed: { label: t("privacyMixed"), meaning: t("privacyMeaningMixed") },
    "no-known-link": {
      label: t("privacyNoKnownLink"),
      meaning: t("privacyMeaningNoKnownLink"),
    },
    unknown: {
      label: t("privacyUnknown"),
      meaning: t("privacyMeaningUnknown"),
    },
  };
  const tips: readonly { text: string; why: string }[] = [
    { text: t("privacyTipNewSpending"), why: t("privacyTipNewSpendingWhy") },
    { text: t("privacyTipAmounts"), why: t("privacyTipAmountsWhy") },
    { text: t("privacyTipWaiting"), why: t("privacyTipWaitingWhy") },
    { text: t("privacyTipOwnMoves"), why: t("privacyTipOwnMovesWhy") },
  ];
  const newSpendingButton = newSpending.offered && (
    <span
      className="privacy-learn__action"
      data-tooltip-id="global-tip"
      data-tooltip-content={newSpending.reason}
      data-tooltip-hidden={!newSpending.reason || undefined}>
      <Button
        type="button"
        className="button--sm button--outline"
        label={t("privacyNewSpending")}
        disabled={newSpending.disabled}
        onClick={newSpending.open}
      />
    </span>
  );

  return (
    <div
      ref={root}
      className="privacy-learn"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}>
      <div className="modal__header">
        <h2 id={titleId}>{t("privacyTitle")}</h2>
      </div>
      <p className="privacy-learn__intro fs-14 color-grey">
        {t("privacyLearnIntro")}{" "}
        <Explainer tip={t("privacyLearnLocalTip")}>
          {t("privacyLearnLocal")}
        </Explainer>
      </p>

      <h3 className="privacy-learn__heading fs-14 fw-500 color-white">
        {t("privacyAssetsTitle")}
      </h3>
      <ul className="privacy-learn__list">
        {items.map(({ asset, view }) => {
          const lines = reasonLines(t, view, { generic: focused });
          return (
            <li
              key={asset.denom || asset.symbol}
              className="privacy-learn__asset">
              <p className="privacy-learn__asset-head">
                <span className="privacy-learn__icon" aria-hidden="true">
                  <IconDenom denom={asset.symbol} />
                </span>
                <span className="privacy-learn__symbol fw-600 color-white">
                  {asset.symbol}
                </span>
                <span
                  className={`privacy-learn__level fw-500 ${TONE_COLOR[view.tone]}`}>
                  {levelLabel(t, view)}
                </span>
                {view.partlyPublic && (
                  <span className="privacy-chip">
                    {t("privacyPartlyPublic")}
                  </span>
                )}
              </p>
              {lines.length > 0 && (
                <ul className="privacy-learn__reasons fs-14 color-grey">
                  {lines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      <p className="privacy-learn__checked fs-12 color-grey">
        {checkedText(t, privacy, now, language)}
      </p>

      {weak && (
        <div className="privacy-learn__advice">
          <p className="fs-14 color-lightGrey">{t("privacyAdviceLimited")}</p>
          {newSpendingButton}
        </div>
      )}

      <h3 className="privacy-learn__heading fs-14 fw-500 color-white">
        {t("privacyLevelsTitle")}
      </h3>
      <ul className="privacy-learn__legend fs-14">
        {LEVELS.map((tone) => (
          <li key={tone} className="privacy-learn__key">
            <span
              className={`privacy-meter privacy-meter--${tone} privacy-learn__sample`}
              aria-hidden="true">
              <PrivacyTrack tone={tone} />
            </span>
            <Explainer
              tip={levels[tone].meaning}
              className={`privacy-learn__level fw-500 ${TONE_COLOR[tone]}`}>
              {levels[tone].label}
            </Explainer>
          </li>
        ))}
        <li className="privacy-learn__key">
          <Explainer tip={t("privacyMeaningPartlyPublic")}>
            <span className="privacy-chip">{t("privacyPartlyPublic")}</span>
          </Explainer>
        </li>
      </ul>
      <p className="privacy-learn__scale fs-12 color-grey">
        {t("privacyScale")}
      </p>

      <h3 className="privacy-learn__heading fs-14 fw-500 color-white">
        {t("privacyTipsTitle")}
      </h3>
      <ul className="privacy-learn__tips fs-14 color-grey">
        {tips.map(({ text, why }) => (
          <li key={text}>
            <Explainer tip={why}>{text}</Explainer>
          </li>
        ))}
      </ul>
    </div>
  );
};
